
import React, { useState, useEffect, useContext, createContext, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { updateSigninStats } from '@/utils/authSecurity';
import { clearImageCache } from '@/lib/imageCache';
import { isDespiaIOS } from '@/hooks/use-despia';

import { onProfileSelectionComplete } from '@/lib/profile-selection-event';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isLoading: boolean;
  profileSelectionComplete: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileSelectionComplete, setProfileSelectionComplete] = useState(() => {
    // On Despia iOS, profile selection is handled by IOSFlow — always consider it complete
    if (isDespiaIOS()) return true;
    return sessionStorage.getItem('profile-selection-shown') === 'true';
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Use refs for values needed inside the auth listener so we don't
  // tear down / re-create the subscription on every route change.
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  useEffect(() => { locationRef.current = location; }, [location]);

  // ─── Mount auth listener ONCE ───
  useEffect(() => {
    let initialSessionResolved = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);

        // Resolve loading on first event
        if (!initialSessionResolved) {
          initialSessionResolved = true;
          setLoading(false);
        }
        if (event !== 'INITIAL_SESSION') {
          setLoading(false);
        }

        // Handle successful sign in - update stats
        if (event === 'SIGNED_IN' && session?.user?.id) {
          setTimeout(async () => {
            try {
              await updateSigninStats(session.user.id);
            } catch (error) {
              console.error('Failed to update signin stats:', error);
            }
          }, 0);

          // Persist V2 onboarding draft (name/avatar/preferences) into profile.
          // Best-effort, bounded — must not block auth.
          setTimeout(() => {
            import('@/lib/save-v2-onboarding-draft')
              .then(({ saveV2OnboardingDraft }) => saveV2OnboardingDraft(session.user.id))
              .catch((e) => console.warn('[AuthProvider] save onboarding draft failed:', e));
          }, 0);


          // AppsFlyer identity + (signup-only) registration event.
          // Runs alongside the RevenueCat→AppsFlyer revenue pipe; no purchase
          // events are fired client-side to avoid double-counting.
          setTimeout(() => {
            import('@/lib/appsflyer-identity').then(({ syncAppsFlyerOnAuth }) => {
              syncAppsFlyerOnAuth(session.user, '[AuthProvider]');
            }).catch(() => {});
          }, 0);

          clearImageCache();

          // On Despia iOS, profile selection is handled by IOSFlow — never reset to false
          if (!isDespiaIOS()) {
            const profileSelectionShown = sessionStorage.getItem('profile-selection-shown');
            setProfileSelectionComplete(profileSelectionShown === 'true');
          }
          
          // Don't hijack navigation for public flow paths — they own their own routing
          const currentPath = locationRef.current.pathname;
          const isOnFlow =
            currentPath === '/onboarding' ||
            currentPath === '/auth' ||
            currentPath === '/subscribe' ||
            currentPath.startsWith('/onboarding/') ||
            currentPath.startsWith('/auth/') ||
            currentPath.startsWith('/subscribe/');
          if (currentPath === '/auth' && !isOnFlow) {
            navigateRef.current('/', { replace: true });
          }
        }

        if (event === 'PASSWORD_RECOVERY') {
          console.log('Password recovery initiated');
        }

        if (event === 'SIGNED_IN' && session?.user?.email_confirmed_at) {
          // Skip navigation if on a public flow path — they handle their own post-auth routing
          const currentPath = locationRef.current.pathname;
          const isOnFlow =
            currentPath === '/onboarding' ||
            currentPath === '/auth' ||
            currentPath === '/subscribe' ||
            currentPath.startsWith('/onboarding/') ||
            currentPath.startsWith('/auth/') ||
            currentPath.startsWith('/subscribe/');
          if (!isOnFlow) {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('type') === 'signup') {
              window.history.replaceState({}, document.title, window.location.pathname);
              navigateRef.current('/', { replace: true });
            }
          }
        }


        if (event === 'SIGNED_OUT') {
          localStorage.removeItem('profile-selection-shown');
          sessionStorage.removeItem('processing-payment');
          sessionStorage.removeItem('profile-selection-shown');
          if (isDespiaIOS()) {
            setTimeout(() => {
              import('despia-native')
                .then(({ default: despia }) => {
                  void Promise.race([
                    despia('revenuecat://logout'),
                    new Promise((resolve) => setTimeout(resolve, 1500)),
                  ]).catch(() => {});
                })
                .catch(() => {});
            }, 0);
          }
          // pending-purchase legacy removed (soft paywall doesn't use payment-first markers)
          import('@/lib/appsflyer-identity')
            .then(({ resetAppsFlyerIdentityCache }) => resetAppsFlyerIdentityCache())
            .catch(() => {});
          clearImageCache();
          setProfileSelectionComplete(false);
          // Sign-out always returns to onboarding.
          navigateRef.current('/onboarding', { replace: true });

        }

      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session check:', session?.user?.id);
      if (!initialSessionResolved) {
        initialSessionResolved = true;
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
      
      if (session) {
        // On Despia iOS, profile selection is handled by IOSFlow — never reset to false
        if (!isDespiaIOS()) {
          const profileSelectionShown = sessionStorage.getItem('profile-selection-shown');
          setProfileSelectionComplete(profileSelectionShown === 'true');
        }
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Mount once — refs handle dynamic values

  // Watch for profile selection changes (same-window custom event + cross-tab storage event)
  useEffect(() => {
    // On Despia iOS, profile selection is always complete — skip sessionStorage checks
    if (isDespiaIOS()) return;

    const checkProfileSelection = () => {
      const profileSelectionShown = sessionStorage.getItem('profile-selection-shown');
      setProfileSelectionComplete(profileSelectionShown === 'true');
    };

    // Check on mount
    checkProfileSelection();
    
    // Listen for cross-tab storage events
    window.addEventListener('storage', checkProfileSelection);
    // Listen for same-window custom event
    const unsubCustom = onProfileSelectionComplete(checkProfileSelection);
    
    return () => {
      window.removeEventListener('storage', checkProfileSelection);
      unsubCustom();
    };
  }, []); // Mount once — no need for location.pathname dep

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading, 
      isLoading: loading, 
      profileSelectionComplete 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
